import React, {useEffect, useMemo, useState} from 'react';
import {Button, Stack, ListGroup, Card, CloseButton, Figure, Form} from "react-bootstrap";
import {Link, useNavigate, useParams} from "react-router-dom";
import {ButtonDelete, CheckText, ToggleText} from "../../../_components";
import {ColumnizeText, FigureText, OurClearAlert, SingleText, SwitchText, PageTitle, SelectText} from "../../../_components";
import {isoTimestampToReadableDate} from "../utilities/readableDate";
import {
    useInfiniteScroll,
    useProduct,
    useProductCount,
    useProductList,
    useTagCount,
    useTagList,
    useMediaCount,
    useMediaList,
    useMediaDetails, useProductDetails
} from "../../../../hooks";
import {config} from "../../../../utils2";
import {DynamicBox, DynamicDialog} from "../../../_components";
import {Formik, Form as FormikForm, FieldArray} from "formik";
import {useAlert, useAuth, useDarkMode} from "../../../../context";
import {useTipCount, useTipList} from "../tip/useTip";
import {WhiteCard} from "../../../device/_components/WhiteCard";
import {centsToString} from "../utilities/formatCurrency";


export function AddEditProduct() {
    const {pid} = useParams()
    const {userId} = useAuth()
    const {success} = useAlert()
    const navigate = useNavigate()

    const {
        value,
        validationSchema,
        statusMap,
        addEditItem,
        deleteItem,
        errorValue,
        clearErrorValue,
    } = useProduct(userId, pid)

    const edit = pid !== "add"

    let digitalType = null
    if (edit && value.is_digital && value.digital_duration > 0 && value.subscription_mb > 0) {
        digitalType = "1"
    } else {
        digitalType = "0"
    }

    const toggleBackorder = [
        {id: false, name: "Out of Stock", variant: "secondary", disabled: false},
        {id: true, name: "Backorder", variant: "warning", disabled: false}
    ]

    const toggleProductType = [
        {id: false, name: "Physical", variant: "primary", disabled: false},
        {id: true, name: "Digital", variant: "info", disabled: false}
    ]

    const subscriptionType = [
        {value: 0, name: "-- select digital type --", disabled: false},
        {value: 1, name: "Subscription for space", disabled: false},
        {value: 2, name: "Subscription for icecast", disabled: true}
    ]

    function handleSubmit(values, actions) {
        addEditItem(values)
            .then(msg => {
                success(msg)
                navigate(-1)
            })
            .catch(console.warn)
            .finally(() => actions.setSubmitting(false))
    }

    function handleOnDelete() {
        deleteItem()
            .then(msg => {
                success(msg)
                navigate(-1)
            })
            .catch(console.warn)
    }

    const capitalizeString = str => str.charAt(0).toUpperCase() + str.slice(1)

    const centsToPrices = prices => prices.map(o => ({...o, price: centsToString(o.price)}))

    const valueConverted = {...value, prices: centsToPrices(value.prices), digital_type: digitalType}

    return (
        <>
            <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>

            <PageTitle title="Product Details">{edit ? "Edit" : "Add"} product.</PageTitle>

            <Formik validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={valueConverted} enableReinitialize={true}>
                {({isSubmitting, resetForm, values}) => (
                    <FormikForm>

                        <WhiteCard>
                            <SingleText name="title" autoFocus/>
                            <SingleText name="summary" type="textarea" rows={1} label="Summary" tip="Enrich text with Markdown" className="" maxLength={160}/>
                            <SingleText name="description" type="textarea" rows={10} label="Description" className=""
                                        tip="Enrich text with Markdown. Add image as ![name-to-visualize | maxWidth=70%](/public/media/filename.webp). Add PDF: substitute 'blob' with 'raw' [name-to-visualize](https://github.com/altrabits/AB_Documents/raw/main/Guide/pocket/filename.pdf)"/>
                            <SingleText name="features" label="Features" type="textarea" rows={10} tip="Enrich text with Markdown" className=""/>
                        </WhiteCard>

                        <WhiteCard>
                            <ColumnizeText className="">
                                <SingleText name="SKU" className=""/>
                                <SingleText name="stock" label="Quantity in stock" className=""/>
                            </ColumnizeText>
                            <ColumnizeText className="my-2">
                                {/*<SwitchText name="is_available" label="Backorder" tip="On indicates the product is available for backorder." className=""/>*/}
                                <ToggleText name="is_available" label="Backorder" items={toggleBackorder}/>
                                <SelectText name="status" label="Status">
                                    {statusMap.map((item, index) => (
                                        <option key={index} value={item}>{capitalizeString(item)}</option>
                                    ))}
                                </SelectText>
                            </ColumnizeText>
                        </WhiteCard>

                        <WhiteCard>
                            <ToggleText name="is_digital" label="Product Type" items={toggleProductType} className=""/>

                            {/*Subscription digital product section*/}
                            <SelectText name="digital_type" label="Digital type" disabled={!values.is_digital} className="my-2">
                                {subscriptionType.map((item, index) => (
                                    <option key={index} value={item.value} disabled={item.disabled}>{item.name}</option>
                                ))}
                            </SelectText>

                            {(values.is_digital && values.digital_type === "1") && <ColumnizeText className="my-2">
                                <SingleText name="digital_duration" label="Subscription Duration" postfix="days" className=""/>
                                <SingleText name="subscription_mb" label="Subscription Bytes" postfix="MB" className=""/>
                            </ColumnizeText>}
                        </WhiteCard>

                        <WhiteCard>
                            <ColumnizeText>
                                <SingleText name="note" label="Note" type="textarea" tip="Enrich text with Markdown" className=""/>
                                <SingleText name="alert" label="Alert" type="textarea" tip="Enrich text with Markdown" className=""/>
                            </ColumnizeText>
                        </WhiteCard>

                        <WhiteCard>
                            <FieldArray name="prices" component={LinkedPrices}/>
                            <div className="text-muted">Price VAT excluded</div>
                        </WhiteCard>

                        <FieldArray name="medias" component={LinkedMedias}/>

                        <FieldArray name="tags" component={LinkedTags}/>

                        <FieldArray name="related" component={LinkedProducts}/>

                        <FieldArray name="tips" component={LinkedTips}/>

                        <Stack className="my-2 text-secondary">
                            {value.created_at && <span>Created at: {isoTimestampToReadableDate(value.created_at)}</span>}
                            {value.updated_at && <span>Updated at: {isoTimestampToReadableDate(value.updated_at)}</span>}
                        </Stack>

                        <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
                            <Button as={Link} variant="link" to={-1}>Go back</Button>
                            <Button type="reset" variant="secondary" onClick={resetForm}>Reset</Button>
                            {edit && <ButtonDelete variant="danger" onDelete={handleOnDelete} value={value.name}>Delete</ButtonDelete>}
                            <Button type="submit" variant="success" disabled={isSubmitting}>Save</Button>
                        </Stack>

                    </FormikForm>
                )}
            </Formik>
        </>
    );
}

function LinkedPrices({push, remove, form}) {
    const isInvalid = typeof form.errors.prices === "string"


    return (
        <DynamicBox label="Link prices" isInvalid={isInvalid} feedback={form.errors.prices} button="Price" className=""
                    onClick={() => push({price: 0, quantity: 1, discount: 0})}>
            {form.values.prices.map((item, idx) =>
                <Card key={idx} style={{width: '18rem'}} className="mb-1">
                    <Card.Body>
                        <SingleText name={`prices.${idx}.price`} label="Price" prefix="€" className="mt-2"/>
                        <SingleText name={`prices.${idx}.quantity`} label="Quantity" prefix="Qty" className="mt-2"/>
                        <SingleText name={`prices.${idx}.discount`} label="Discount" prefix="%" className="mt-2"/>

                        <CloseButton className="position-absolute top-0 end-0 p-2" onClick={() => remove(idx)}/>
                    </Card.Body>
                </Card>
            )}
        </DynamicBox>
    )
}

function LinkedMedias({move, push, remove, form}) {
    const [show, setShow] = useState(false)

    const isInvalid = typeof form.errors.medias === "string"

    return (<WhiteCard>
        <DynamicBox label="Link medias" isInvalid={isInvalid} feedback={form.errors.medias} button="Media" className="" onClick={() => setShow(true)}>
            {form.values.medias.map((item, idx) => (
                <LinkedMediasCard key={idx} idx={idx} item={item} move={move} remove={remove} idxMax={form.values.medias.length - 1}/>
            ))}
        </DynamicBox>
        <LinkedMediasDialog show={show} onHide={() => setShow(false)} push={push} form={form}/>
    </WhiteCard>)
}

function LinkedMediasCard({item, idx, move, remove, idxMax}) {
    const {value} = useMediaDetails(item?.id)
    const {darkMode} = useDarkMode()

    return <Card key={idx} className="position-relative mb-1" style={{width: '15rem'}}>
        {/*<Card.Img src={config.get_public_media(value.path, "thumb", darkMode)}/>*/}
        <div className="text-center">
            <Figure.Image
                className=""
                src={config.get_public_media(value.path, "thumb", darkMode)}
                alt={item.description}
                height="120px"
                width="120px"
                loading="lazy"
            />
        </div>
        <Card.Body>
            <Card.Text className="text-center"><Link to={`/backend/media/${value.id}`}
                                                     className="link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                {value.description}
            </Link></Card.Text>
        </Card.Body>
        <Card.Footer>
            <Stack direction="horizontal" className="justify-content-between" gap={1}>
                <Button variant="outline-secondary" className="bi bi-arrow-left" size="sm" onClick={() => move(idx, idx - 1)} disabled={idx === 0}></Button>
                <Button variant="outline-danger" className="bi bi-trash" size="sm" onClick={() => remove(idx)}></Button>
                <Button variant="outline-secondary" className="bi bi-arrow-right" size="sm" onClick={() => move(idx, idx + 1)} disabled={idx === idxMax}></Button>
            </Stack>
        </Card.Footer>
    </Card>
}

function LinkedMediasDialog({show, onHide, push, form}) {
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useMediaCount()
    const {list, loadingList} = useMediaList(pageSize, pageIndex, null, "desc_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => !form.values.medias.find(item2 => item2.id === item.id)), [list, form.values.medias])
    const renderList = useMemo(() => tempList.filter(item => item.description.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])

    return <DynamicDialog title="Select Media" show={show} onHide={onHide} filter={filter} setFilter={setFilter}>
        <div>Loaded {list.length - form.values.medias.length} of {count - form.values.medias.length}</div>
        {renderList && renderList.length > 0 ?
            renderList.map((item, index) => <ListGroup.Item key={item.id} eventKey={item.id} ref={renderList.length === index + 1 ? lastElementRef : null}>
                <FigureText path={list.find(media => media.id === item.id)?.path} alt={item.description}
                            onClick={() => push(item)}>
                    <div>
                        <h5>{item.description}</h5>
                        <span>{item.type}</span>
                    </div>
                </FigureText>
            </ListGroup.Item>)
            :
            <span className="text-center">No image found</span>
        }
    </DynamicDialog>
}

function LinkedTags({push, remove, form}) {
    const [show, setShow] = useState(false)
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useTagCount()
    const {list, loadingList} = useTagList(pageSize, pageIndex, null, "name_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => !form.values.tags.find(item2 => item2.id === item.id)), [list, form.values.tags])
    const renderList = useMemo(() => tempList.filter(item => item.name.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])
    const selectedItems = useMemo(() => form.values.tags.sort((a, b) => a.name.localeCompare(b.name)), [form.values.tags])

    const isInvalid = typeof form.errors.tags === "string"

    return (
        <WhiteCard>
            <DynamicBox label="Link tags" isInvalid={isInvalid} feedback={form.errors.tags} button="Tag" className=""
                        onClick={() => setShow(true)}>
                {selectedItems.map((item, index) => (
                    <Button key={index} className="mb-1" onClick={() => remove(index)} variant={item.variant}>
                        {item.name}<i className="bi bi-x-lg"/>
                    </Button>
                ))}
            </DynamicBox>
            <DynamicDialog title="Select Tag" show={show} onHide={() => setShow(false)} filter={filter} setFilter={setFilter}>
                <div>Loaded {list.length - selectedItems.length} of {count - selectedItems.length}</div>
                {renderList && renderList.length > 0 ?
                    renderList.map((item, index) => (
                        <ListGroup.Item key={item.id} eventKey={item.id} ref={renderList.length === index + 1 ? lastElementRef : null}>
                            <Button variant={item.variant} onClick={() => push(item)} className="mb-2">{item.name}</Button>
                        </ListGroup.Item>
                    ))
                    :
                    <span className="text-center">No tag found</span>
                }
            </DynamicDialog>
        </WhiteCard>
    )
}

function LinkedProducts({move, remove, push, form}) {

    const name = "related"
    const [show, setShow] = useState(false)

    return <WhiteCard>
        <DynamicBox className="" label="Link products" isInvalid={typeof form.errors[name] === "string"} feedback={form.errors[name]} button="Product"
                    onClick={() => setShow(true)}>
            {form.values[name].map((item, idx) => <LinkedProductCard key={idx} idx={idx} item={item} move={move} remove={remove} idxMax={form.values[name].length - 1}/>)}
        </DynamicBox>
        <LinkedProductsDialog show={show} onHide={() => setShow(false)} push={push} selected={form.values[name]}/>
    </WhiteCard>
}

function LinkedProductCard({item, idx, move, remove, idxMax}) {

    const {value, loadingValue, errorValue, clearError} = useProductDetails(item?.id)
    const {darkMode} = useDarkMode()

    return <>
        <OurClearAlert variant="danger" msg={errorValue} onClear={clearError}/>

        {loadingValue ?
            <div>Loading...</div>
            : <Card className="mb-1" style={{width: '25rem'}}>
                {/*<Card.Img variant="top" src={config.get_public_media(value?.medias[0]?.path, "thumb", darkMode)}/>*/}
                <div className="text-center">
                    <Figure.Image
                        className=""
                        src={config.get_public_media(value?.medias[0]?.path, "thumb", darkMode)}
                        alt={item.description}
                        height="120px"
                        width="120px"
                        loading="lazy"
                    />
                </div>
                <Card.Body className="">
                    <Card.Title>{value.title}</Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">{value.SKU}</Card.Subtitle>
                    <Card.Text className="small">{value.summary}</Card.Text>
                </Card.Body>
                <Card.Footer>
                    <Stack direction="horizontal" className="justify-content-between" gap={1}>
                        <Button variant="outline-secondary" className="bi bi-arrow-left" size="sm" onClick={() => move(idx, idx - 1)} disabled={idx === 0}></Button>
                        <Button variant="outline-danger" className="bi bi-trash" size="sm" onClick={() => remove(idx)}></Button>
                        <Button variant="outline-secondary" className="bi bi-arrow-right" size="sm" onClick={() => move(idx, idx + 1)} disabled={idx === idxMax}></Button>
                    </Stack>
                </Card.Footer>
            </Card>
        }
    </>
}

function LinkedProductsDialog({show, onHide, push, selected}) {
    const {pid} = useParams()
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useProductCount()
    const {list, loadingList} = useProductList(pageSize, pageIndex, null, "title_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => item.id !== pid && !selected.find(item2 => item2.id === item.id)), [list, pid, selected])
    const renderList = useMemo(() => tempList.filter(item => item.title.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])

    const prodLoaded = renderList.length
    const prodTotal = count - (pid === "add" ? 0 : 1) - selected.length

    return <DynamicDialog title="Select Related Products" show={show} onHide={onHide} filter={filter} setFilter={setFilter}>
        <div>Loaded {prodLoaded} of {prodTotal}</div>
        {renderList && renderList.length > 0 ?
            renderList.map((item, index) =>
                <FigureText key={item.id} ref={renderList.length === index + 1 ? lastElementRef : null} alt={item.id}
                            path={item.medias[0]?.path}
                            onClick={() => push(item)}>
                    <div>
                        <h5>{item.title}</h5>
                        <span className="fw-light">{item.SKU}</span>
                    </div>
                </FigureText>)
            :
            <span className="text-center">No product found</span>
        }
    </DynamicDialog>
}


function LinkedTips({push, remove, form}) {
    const [show, setShow] = useState(false)
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useTipCount()
    const {list, loadingList} = useTipList(pageSize, pageIndex, null, "title_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => !form.values.tips.find(item2 => item2.id === item.id)), [list, form.values.tips])
    const renderList = useMemo(() => tempList.filter(item => item.title.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])
    const selectedItems = useMemo(() => form.values.tips.sort((a, b) => a.title.localeCompare(b.title)), [form.values.tips])

    const isInvalid = typeof form.errors.tips === "string"

    return (
        <WhiteCard>
            <DynamicBox label="Link tips" isInvalid={isInvalid} feedback={form.errors.tips} button="Tip" className=""
                        onClick={() => setShow(true)}>
                {selectedItems.map((item, index) => (
                    <Button key={index} className="mb-1" onClick={() => remove(index)} variant="primary">
                        {item.title}<i className="bi bi-x-lg"/>
                    </Button>
                ))}
            </DynamicBox>
            <DynamicDialog title="Select Tip" show={show} onHide={() => setShow(false)} filter={filter} setFilter={setFilter}>
                <div>Loaded {list.length - selectedItems.length} of {count - selectedItems.length}</div>
                {renderList && renderList.length > 0 ?
                    renderList.map((item, index) => (
                        <ListGroup.Item key={item.id} eventKey={item.id} ref={renderList.length === index + 1 ? lastElementRef : null}>
                            <div className="d-flex flex-column mb-2">
                                <Button variant="link" onClick={() => push(item)}
                                        className="p-0 link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">{item.title}</Button>
                                <small className="text-body-secondary">{item.description}</small>
                            </div>
                        </ListGroup.Item>
                    ))
                    :
                    <span className="text-center">No tip found</span>
                }
            </DynamicDialog>
        </WhiteCard>
    )
}
